import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'
import { defaultLanguage, Language, supportedLanguages, translations } from './translations'

type TranslationParams = Record<string, string | number>

type LanguageContextValue = {
  language: Language
  setLanguage: (language: Language) => void
  t: (key: string, params?: TranslationParams) => string
}

const storageKey = 'verifyvesselcertificate-language'

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined)

const isSupportedLanguage = (language: string | null): language is Language =>
  supportedLanguages.includes(language as Language)

const getInitialLanguage = (): Language => {
  if (typeof window === 'undefined') {
    return defaultLanguage
  }

  const storedLanguage = window.localStorage.getItem(storageKey)
  return isSupportedLanguage(storedLanguage) ? storedLanguage : defaultLanguage
}

const getTranslationValue = (language: Language, key: string) => {
  const value = key.split('.').reduce<unknown>((current, part) => {
    if (current && typeof current === 'object' && part in current) {
      return (current as Record<string, unknown>)[part]
    }

    return undefined
  }, translations[language])

  return typeof value === 'string' ? value : key
}

const interpolate = (value: string, params?: TranslationParams) => {
  if (!params) {
    return value
  }

  return Object.entries(params).reduce(
    (current, [key, replacement]) => current.split(`{{${key}}}`).join(String(replacement)),
    value
  )
}

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage)

  useEffect(() => {
    document.documentElement.lang = language
    window.localStorage.setItem(storageKey, language)
  }, [language])

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      setLanguage: setLanguageState,
      t: (key, params) => interpolate(getTranslationValue(language, key), params),
    }),
    [language]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)

  if (!context) {
    throw new Error('useLanguage must be used inside LanguageProvider')
  }

  return context
}

export const useLanguageToggle = () => {
  const { language, setLanguage, t } = useLanguage()
  const nextLanguage: Language = language === 'nb' ? 'en' : 'nb'

  return {
    ariaLabel: language === 'nb' ? t('language.switchToEnglish') : t('language.switchToNorwegian'),
    label: t('language.toggleLabel'),
    toggleLanguage: () => setLanguage(nextLanguage),
  }
}
